import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-concrete-white flex" data-testid="login-page">
      {/* Image Side */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1635949602587-7c970c33ffb3?w=1200&q=80"
          alt="Basketball"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-deep-obsidian/60" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="font-anton text-4xl text-white tracking-tight">VINTAGE</span>
              <span className="font-bebas text-4xl text-chicago-red">KICKS</span>
            </div>
            <p className="text-cement-grey text-lg max-w-md">
              Access your account to track orders, manage wishlists, and get exclusive drops.
            </p>
          </div>
        </div>
      </div>

      {/* Form Side */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <span className="font-anton text-3xl tracking-tight">VINTAGE</span>
            <span className="font-bebas text-3xl text-chicago-red">KICKS</span>
          </div>

          <div className="text-center mb-8">
            <h1 className="font-anton text-4xl tracking-tighter uppercase">WELCOME BACK</h1>
            <p className="text-cement-grey mt-2">Sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="font-bebas tracking-wider">EMAIL</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="mt-1 rounded-sm"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-bebas tracking-wider">PASSWORD</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 rounded-sm"
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-sm font-bold py-6 rounded-sm btn-primary"
              data-testid="login-submit"
            >
              {loading ? (
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <p className="text-center text-cement-grey mt-8">
            Don't have an account?{' '}
            <Link to="/signup" className="text-chicago-red font-bold hover:underline" data-testid="signup-link">
              Create one
            </Link>
          </p>

          {/* Demo credentials hint */}
          <div className="mt-8 p-4 bg-gray-100 rounded-sm text-sm">
            <p className="font-bold mb-1">Admin Demo:</p>
            <p className="text-cement-grey">Email: admin@vintagekicks.com</p>
            <p className="text-cement-grey">Password: admin123</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
